import type { TFillRef } from "@libs/figure-spans";
import $api from "@/zapi-frontend/shared/api/axios";
import type { TLeoMessageImage } from "@/zapi-frontend/stores/leoChatStore";

// An uploaded image, as the start route takes it back.
export type TLeoImageRef = { path: string; name: string };

export type TStartLeoChatBody = {
  projectId: number;
  message: string;
  canEdit: boolean;
  launch: "cli" | "token";
  chapterSlug?: string;
  slideIndex?: number;
  images?: TLeoImageRef[];
};

export type TLeoChatHistoryItem = {
  role: "user" | "assistant";
  content: string;
  appliedChapters: string[] | null;
  appliedSlides?: number[] | null;
  estCostUsd: number | null;
  isError: boolean;
  refs?: TFillRef[] | null;
  images?: TLeoMessageImage[];
};

export type TLeoChatProgress = {
  assistantTurns: number;
  toolCalls: number;
  lastEvent: string | null;
  lastAction?: string | null;
  // From the turn's transcript: the last tool call, which the model's next text does not overwrite,
  // and every chapter file edited so far.
  lastTool?: string | null;
  editedFiles?: string[];
};

export type TLeoChatTaskStatus = {
  status: "pending" | "running" | "succeeded" | "failed" | string;
  progress?: TLeoChatProgress | null;
  estCostUsd?: number | null;
  reply?: string | null;
  replyRefs?: TFillRef[] | null;
  appliedChapters?: string[];
  appliedSlides?: number[];
  error?: string | null;
};

type TEnvelope<T> = { status: number; data: T; message?: string };

export async function startLeoChatTask(body: TStartLeoChatBody): Promise<string> {
  const response = await $api.post<TEnvelope<{ id: string }>>("/chat/claude-chat", body);
  return response.data.data.id;
}

export async function uploadLeoImage(projectId: string, file: File): Promise<TLeoImageRef> {
  const form = new FormData();
  form.append("file", file);
  const response = await $api.post<TEnvelope<TLeoImageRef>>(`/chat/claude-chat/project/${projectId}/images`, form, {
    headers: { "Content-Type": "multipart/form-data" }
  });
  return response.data.data;
}

export async function getLeoChatTaskStatus(taskId: string): Promise<TLeoChatTaskStatus> {
  const response = await $api.get<TEnvelope<TLeoChatTaskStatus>>(`/chat/claude-chat/${taskId}`);
  return response.data.data;
}

export async function cancelLeoChatTask(taskId: string): Promise<void> {
  await $api.post(`/chat/claude-chat/${taskId}/cancel`);
}

export type TActiveLeoChatTask = {
  id: string | null;
  canEdit: boolean | null;
  status: string | null;
  mode: string | null;
  createdAt?: string | null;
};

export async function getActiveLeoChatTask(projectId: string): Promise<TActiveLeoChatTask> {
  const response = await $api.get<TEnvelope<TActiveLeoChatTask>>(`/chat/claude-chat/project/${projectId}/active`);
  return response.data.data;
}

export async function getLeoChatHistory(projectId: string): Promise<TLeoChatHistoryItem[]> {
  const response = await $api.get<TEnvelope<TLeoChatHistoryItem[]>>(`/chat/claude-chat/project/${projectId}/history`);
  return response.data.data;
}

export async function clearLeoChatHistory(projectId: string): Promise<void> {
  await $api.delete(`/chat/claude-chat/project/${projectId}/history`);
}
